import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useLocation } from 'react-router-dom'

import { EuiButton, EuiFieldPassword, EuiFormRow, EuiPanel, EuiSpacer, EuiText, EuiTitle, EuiCallOut, EuiFieldText } from '@elastic/eui'

import { ApiError, apiAcceptOrganizationInvite, apiGetOrganizationInvite } from '../../api/adcritterApi'
import history from '../../services/HistoryService'
import { useWhiteLabel } from '../../whiteLabel/WhiteLabelContext'

export const UserInviteAccept: React.FC = () => {
  const { search } = useLocation()
  const query = new URLSearchParams(search)
  const whiteLabel = useWhiteLabel()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [generalError, setGeneralError] = useState('')

  const [email, setEmail] = useState('')
  const [organizationName, setOrganizationName] = useState('')

  const organizationId = query.get('organization')
  const code = query.get('code')

  useEffect(() => {
    if (organizationId && code) {
      apiGetOrganizationInvite(organizationId, code).then(response => {
        setEmail(response.data.email ?? '')
        setOrganizationName(response.data.organizationName ?? '')
      })
    }
  }, [organizationId, code])

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!password) {
      newErrors.password = 'Password is required'
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords must match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleAcceptClick = async () => {
    if (!validateForm() || !organizationId || !code) return

    setIsSubmitting(true)
    setGeneralError('')

    try {
      const response = await apiAcceptOrganizationInvite(organizationId, code, password)

      if (response.errors) {
        const newErrors: { [key: string]: string } = {}
        response.errors.forEach((error: ApiError) => {
          if (error.name) {
            newErrors[error.name] = error.message
          } else {
            setGeneralError(error.message)
          }
        })
        setErrors(newErrors)
      } else {
        setIsSuccess(true)
      }
    } catch (error) {
      setGeneralError('An unexpected error occurred. Please try again.')
    }

    setIsSubmitting(false)
  }

  if (!organizationId || !code) {
    return (
      <div className='login'>
        <div style={{ height: 400, width: 350, gridRow: '2/3' }}>
          <EuiPanel hasShadow={true}>
            <EuiCallOut title='Invalid Invitation Link' color='danger' iconType='alert'>
              <p>This invitation link is invalid or has expired. Please contact your administrator for a new invitation.</p>
              <EuiSpacer size='m' />
              <EuiButton fill onClick={() => history.push('/login')}>
                Go to Login
              </EuiButton>
            </EuiCallOut>
          </EuiPanel>
        </div>
      </div>
    )
  }

  if (isSuccess) {
    return (
      <React.Fragment>
        <Helmet>
          <title>Invitation Accepted</title>
          <title>Invitation Accepted</title>
        </Helmet>
        <div className='login'>
          <div style={{ height: 400, width: 350, gridRow: '2/3' }}>
            <div style={{ textAlign: 'center' }}>
              <img src={whiteLabel?.getSquareLogo()} alt={whiteLabel?.getName()} style={{ marginBottom: 25, width: 300 }} />
            </div>
            <EuiPanel hasShadow={true}>
              <EuiCallOut title='Account Created Successfully!' color='success' iconType='check'>
                <p>Your account has been created. You can now log in with your new credentials.</p>
                <EuiSpacer size='m' />
                <EuiButton fill onClick={() => history.push('/login')}>
                  Go to Login
                </EuiButton>
              </EuiCallOut>
            </EuiPanel>
          </div>
        </div>
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Create Password</title>
      </Helmet>
      <div className='login'>
        <div style={{ height: 500, width: 350, gridRow: '2/3' }}>
          <div style={{ textAlign: 'center' }}>
            <img src={whiteLabel?.getSquareLogo()} alt={whiteLabel?.getName()} style={{ marginBottom: 25, width: 300 }} />
          </div>
          <EuiPanel hasShadow={true}>
            <EuiTitle>
              <h2>Create Password</h2>
            </EuiTitle>
            <EuiSpacer />
            <EuiText size='s'>
              <p>Simply create a password to get started.</p>
            </EuiText>
            <EuiSpacer />
            {generalError && (
              <React.Fragment>
                <EuiCallOut title='Error' color='danger' size='s'>
                  {generalError}
                </EuiCallOut>
                <EuiSpacer size='s' />
              </React.Fragment>
            )}
            <EuiFormRow label='Email'>
              <EuiFieldText value={email} disabled />
            </EuiFormRow>
            <EuiFormRow label='Password' error={errors.password} isInvalid={!!errors.password}>
              <EuiFieldPassword value={password} onChange={e => setPassword(e.target.value)} isInvalid={!!errors.password} autoComplete='new-password' />
            </EuiFormRow>

            <EuiFormRow label='Confirm Password' error={errors.confirmPassword} isInvalid={!!errors.confirmPassword}>
              <EuiFieldPassword value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} isInvalid={!!errors.confirmPassword} autoComplete='new-password' />
            </EuiFormRow>

            <EuiSpacer />

            <EuiButton onClick={handleAcceptClick} fill isLoading={isSubmitting}>
              Accept Invitation
            </EuiButton>
          </EuiPanel>
        </div>
      </div>
    </React.Fragment>
  )
}
